import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function SocialMediaStrategyPage() {
  const platforms = [
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      bestFor: 'Local community engagement, events, older demographics',
      features: [
        'Business Pages with reviews and ratings',
        'Facebook Groups for community building',
        'Events promotion for El Paso happenings',
        'Facebook Marketplace for local sales',
        'Targeted ads to El Paso residents',
        'Live video for real-time engagement'
      ],
      postingFrequency: '1-2 times daily',
      demographics: 'Ages 25-65+, broad El Paso audience'
    },
    {
      name: 'Instagram',
      icon: 'ri-instagram-line',
      bestFor: 'Visual storytelling, younger audiences, lifestyle brands',
      features: [
        'Photo and video posts showcasing products',
        'Stories for behind-the-scenes content',
        'Reels for short-form video content',
        'Shopping features for e-commerce',
        'Location tags for El Paso visibility',
        'Hashtags for local discovery'
      ],
      postingFrequency: '1 post + 3-5 stories daily',
      demographics: 'Ages 18-44, visual-focused audience'
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      bestFor: 'B2B networking, professional services, recruiting',
      features: [
        'Company Page for business updates',
        'Professional networking in El Paso',
        'Thought leadership articles',
        'Job postings and recruiting',
        'Industry news and insights',
        'Employee advocacy programs'
      ],
      postingFrequency: '3-5 times weekly',
      demographics: 'Professionals, B2B decision-makers'
    },
    {
      name: 'TikTok',
      icon: 'ri-tiktok-line',
      bestFor: 'Viral content, Gen Z audience, entertainment',
      features: [
        'Short-form video content (15-60 seconds)',
        'Trending sounds and challenges',
        'Behind-the-scenes and authentic content',
        'Local El Paso trends and culture',
        'Influencer collaborations',
        'TikTok Shop for product sales'
      ],
      postingFrequency: '1-3 times daily',
      demographics: 'Ages 16-34, entertainment-focused'
    },
    {
      name: 'X (Twitter)',
      icon: 'ri-twitter-x-line',
      bestFor: 'Real-time updates, customer service, news',
      features: [
        'Quick updates and announcements',
        'Customer service responses',
        'Local El Paso news and events',
        'Hashtag campaigns',
        'Polls and engagement',
        'Thread storytelling'
      ],
      postingFrequency: '3-10 times daily',
      demographics: 'Ages 25-49, news-focused audience'
    },
    {
      name: 'YouTube',
      icon: 'ri-youtube-line',
      bestFor: 'Long-form video, tutorials, product demos',
      features: [
        'Educational content and tutorials',
        'Product demonstrations',
        'Customer testimonials',
        'Behind-the-scenes videos',
        'SEO benefits for search',
        'YouTube Shorts for quick content'
      ],
      postingFrequency: '1-2 times weekly',
      demographics: 'All ages, educational content seekers'
    }
  ];

  const contentTypes = [
    {
      type: 'Educational Content',
      examples: 'How-to guides, tips, industry insights',
      engagement: 'High shares, saves',
      icon: 'ri-book-open-line'
    },
    {
      type: 'Behind-the-Scenes',
      examples: 'Team introductions, process videos, day-in-the-life',
      engagement: 'Builds trust, humanizes brand',
      icon: 'ri-camera-line'
    },
    {
      type: 'User-Generated Content',
      examples: 'Customer photos, reviews, testimonials',
      engagement: 'High authenticity, social proof',
      icon: 'ri-user-star-line'
    },
    {
      type: 'Promotional Content',
      examples: 'Sales, new products, special offers',
      engagement: 'Drives conversions',
      icon: 'ri-megaphone-line'
    },
    {
      type: 'Community Content',
      examples: 'Local events, El Paso culture, community involvement',
      engagement: 'Local connection, loyalty',
      icon: 'ri-community-line'
    },
    {
      type: 'Interactive Content',
      examples: 'Polls, quizzes, Q&A sessions, contests',
      engagement: 'High engagement, fun',
      icon: 'ri-question-answer-line'
    }
  ];

  const elPasoTips = [
    {
      title: 'Embrace Bilingual Content',
      description: 'Create posts in both English and Spanish to reach the full El Paso/Juárez market. Use code-switching naturally.',
      icon: 'ri-translate-2'
    },
    {
      title: 'Highlight Local Landmarks',
      description: 'Feature recognizable El Paso locations like Franklin Mountains, UTEP, Downtown, to build local connection.',
      icon: 'ri-map-pin-line'
    },
    {
      title: 'Engage with Local Hashtags',
      description: 'Use #ElPaso, #915, #ElPasoTX, #ElPasoStrong, #ElPasoProud to increase local visibility.',
      icon: 'ri-hashtag'
    },
    {
      title: 'Partner with Local Influencers',
      description: 'Collaborate with El Paso micro-influencers who have engaged local followings.',
      icon: 'ri-user-heart-line'
    },
    {
      title: 'Celebrate Local Events',
      description: 'Post about Sun Bowl, UTEP games, Chalk the Block, Dia de los Muertos, and other El Paso events.',
      icon: 'ri-calendar-event-line'
    },
    {
      title: 'Support Military Community',
      description: 'Acknowledge Fort Bliss and military families with special content and offers.',
      icon: 'ri-shield-star-line'
    }
  ];

  const contentCalendar = [
    { day: 'Monday', focus: 'Motivational/Inspirational', example: 'Monday motivation quote, success story' },
    { day: 'Tuesday', focus: 'Educational/Tips', example: 'Tutorial, how-to guide, industry tip' },
    { day: 'Wednesday', focus: 'Behind-the-Scenes', example: 'Team spotlight, process video' },
    { day: 'Thursday', focus: 'Throwback/Story', example: 'Company history, customer story' },
    { day: 'Friday', focus: 'Fun/Interactive', example: 'Poll, quiz, weekend plans' },
    { day: 'Saturday', focus: 'Community/Local', example: 'El Paso events, local spotlight' },
    { day: 'Sunday', focus: 'User-Generated Content', example: 'Customer photos, testimonials' }
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Social Media Strategy",
        "item": "https://elpasotexasbusiness.com/resources/social-media-strategy"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Social Media Strategy for El Paso Businesses - Facebook, Instagram & TikTok"
        description="Develop an effective social media strategy for your El Paso business. Learn platform selection, content planning, engagement tactics, and bilingual marketing for the border market."
        canonical="https://elpasotexasbusiness.com/resources/social-media-strategy"
        ogImage="https://public.readdy.ai/ai/img_res/9a2b71b246e98778720dab7cf821c838.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold flex items-center space-x-2 whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Social Media Strategy</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Build a powerful social media presence that connects with El Paso customers, drives engagement, and grows your business. Learn platform-specific strategies and local best practices.
              </p>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Platform Selection Guide</h2>
              <div className="space-y-8">
                {platforms.map((platform, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-10">
                    <div className="flex items-start space-x-6 mb-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${platform.icon} text-3xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{platform.name}</h3>
                        <p className="text-base text-gray-600 mb-4">{platform.bestFor}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <span className="text-sm font-semibold text-gray-900">Posting Frequency:</span>
                            <span className="text-sm text-gray-600 ml-2">{platform.postingFrequency}</span>
                          </div>
                          <div>
                            <span className="text-sm font-semibold text-gray-900">Demographics:</span>
                            <span className="text-sm text-gray-600 ml-2">{platform.demographics}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ml-22">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Key Features:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {platform.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-start space-x-3">
                            <i className="ri-checkbox-circle-fill text-[#D2691E] text-xl flex-shrink-0 mt-0.5"></i>
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Content Types That Work</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {contentTypes.map((content, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${content.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{content.type}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">{content.examples}</p>
                    <div className="text-sm font-semibold text-[#D2691E]">{content.engagement}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">El Paso Social Media Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {elPasoTips.map((tip, index) => (
                  <div key={index} className="bg-[#1B3A3A] rounded-2xl p-8 text-white">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${tip.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{tip.title}</h3>
                    <p className="text-sm text-white/80 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Weekly Content Calendar Template</h2>
              <div className="bg-[#FAFAF8] rounded-2xl p-10">
                <div className="space-y-4">
                  {contentCalendar.map((day, index) => (
                    <div key={index} className="flex items-start space-x-6 pb-4 border-b border-gray-200 last:border-0">
                      <div className="w-32 flex-shrink-0">
                        <span className="text-base font-bold text-gray-900">{day.day}</span>
                      </div>
                      <div className="flex-1">
                        <div className="text-base font-semibold text-[#D2691E] mb-1">{day.focus}</div>
                        <div className="text-sm text-gray-600">{day.example}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <strong className="text-gray-900">Pro Tip:</strong> Adjust this template based on your audience engagement data. Track which days and content types perform best, then optimize your calendar accordingly.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-3xl p-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Social Media Success Checklist</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  'Define clear goals (awareness, engagement, sales)',
                  'Choose 2-3 platforms to focus on initially',
                  'Create a consistent posting schedule',
                  'Develop a content calendar 2-4 weeks ahead',
                  'Use high-quality images and videos',
                  'Write compelling captions with clear CTAs',
                  'Engage with comments and messages promptly',
                  'Use relevant hashtags (mix of popular and niche)',
                  'Track analytics and adjust strategy monthly',
                  'Collaborate with local El Paso businesses',
                  'Run occasional paid ads to boost reach',
                  'Stay authentic and true to your brand voice'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-line text-[#D2691E] text-2xl flex-shrink-0"></i>
                    <span className="text-base text-gray-700 pt-0.5">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16 bg-[#1B3A3A] rounded-3xl p-12 text-center">
              <h3 className="text-4xl font-bold text-white mb-4">Need Social Media Help?</h3>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Get personalized guidance on building a social media strategy that works for your El Paso business.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg whitespace-nowrap"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}